import { NgModule, Type } from '@angular/core';
import { RouterModule } from '@angular/router';

import { permissionGuard } from '../../core/guards/permission-guard.service';
import { resolvePageTitle } from '../../core/resolvers/page-title.resolver';
import { BeneficiariesComponent } from '../../modules/beneficiaries/beneficiaries.component';
import { BeneficiaryHistoryComponent } from '../../modules/beneficiaries/beneficiary-history/beneficiary-history.component';
import { BulkActionsComponent } from '../../modules/bulk-actions/bulk-actions.component';
import { FinancialModule } from '../../modules/financial/financial.module';
import { InsurancePoliciesModule } from '../../modules/insurance-policies/insurance-policies.module';
import { MetricsModule } from '../../modules/metrics/metrics.module';
import { Routes } from '../../shared/models/routes/routes.model';
import { RouteUtils } from '../../shared/utils/route/route.util';

import { BatchActionInfoComponent } from './views/batch-action-info/batch-action-info.component';
import { BatchActionsComponent } from './views/batch-actions/batch-actions.component';
import { BeneficiaryCreationComponent } from './views/beneficiary-creation/beneficiary-creation.component';
import { BeneficiaryEditComponent } from './views/beneficiary-edit/beneficiary-edit.component';
import { BeneficiaryRegistrationComponent } from './views/beneficiary-registration/beneficiary-registration.component';
import { BeneficiaryViewComponent } from './views/beneficiary-view/beneficiary-view.component';
import { BenefitRegistrationComponent } from './views/benefit-registration/benefit-registration.component';
import { BenefitsComponent } from './views/benefits/benefits.component';
import { DigitalHospitalComponent } from './views/digital-hospital/digital-hospital.component';
import { OrgHubComponent } from './views/org-hub/org-hub.component';
import { OrgRegistrationComponent } from './views/org-registration/org-registration.component';
import { OrgReportsComponent } from './views/org-reports/org-reports.component';
import { OrgReportsLinkComponent } from './views/org-reports-link/org-reports-link.component';
import { OrganizationsComponent } from './views/organizations/organizations.component';
import { PartnerBrokerRegistrationComponent } from './views/partner-brokers/partner-broker-registration/partner-broker-registration.component';
import { PartnerBrokersComponent } from './views/partner-brokers/partner-brokers.component';
import { PartnerRegistrationComponent } from './views/partner-registration/partner-registration.component';
import { PartnersComponent } from './views/partners/partners.component';
import { PoliciesComponent } from './views/policies/policies.component';
import { PolicyRegistrationComponent } from './views/policy-registration/policy-registration.component';
import { PositionRegistrationComponent } from './views/position-registration/position-registration.component';
import { PositionsComponent } from './views/positions/positions.component';
import { ScoreComponent } from './views/score/score.component';
import { SecurityComponent } from './views/security/security.component';
import { UserRegistrationComponent } from './views/user-registration/user-registration.component';
import { UsersComponent } from './views/users/users.component';

const routes: Routes = [
	{
		data: { pageTitle: { label: 'Administração' } },
		path: '',
		component: OrganizationsComponent,
	},
	{
		data: { pageTitle: { label: 'Cadastrar Organização' } },
		path: 'cadastro',
		component: OrgRegistrationComponent,
	},
	// TODO: Refatorar os componentes com parâmetro ':id' para serem carregados via ':organizationId'
	{
		canActivate: [permissionGuard],
		path: `:${RouteUtils.params.organizationId.value}`,
		children: [
			{
				resolve: {
					pageTitle: resolvePageTitle,
				},
				path: '',
				component: OrgHubComponent,
			},
			{
				path: RouteUtils.endpoints.financial.path,
				loadChildren: async (): Promise<Type<FinancialModule>> =>
					import('../../modules/financial/financial.module').then(
						(loadedModule) => loadedModule.FinancialModule,
					),
			},
			{
				path: 'metricas',
				loadChildren: async (): Promise<Type<MetricsModule>> =>
					import('../../modules/metrics/metrics.module').then(
						(module) => module.MetricsModule,
					),
			},
		],
	},
	// TODO: Código Legado que deve ser movido para o parâmetro :organizationId
	{
		path: ':id',
		children: [
			{
				data: { pageTitle: { label: 'Ações em Massa' } },
				path: 'acoes-em-massa',
				component: BatchActionsComponent,
			},
			{
				data: { pageTitle: { label: 'Ação em Massa' } },
				path: 'acoes-em-massa/:uId',
				component: BatchActionInfoComponent,
			},
			{
				path: 'apolices',
				loadChildren: async (): Promise<Type<InsurancePoliciesModule>> =>
					import(
						'../../modules/insurance-policies/insurance-policies.module'
					).then((module) => module.InsurancePoliciesModule),
			},
			{
				data: { pageTitle: { label: 'Beneficiários' } },
				path: 'beneficiarios',
				component: BeneficiariesComponent,
			},
			{
				data: { pageTitle: { label: 'Movimentações em Massa' } },
				path: 'movimentacoes-em-massa',
				component: BulkActionsComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Beneficiário' } },
				path: 'beneficiarios/cadastro',
				component: BeneficiaryCreationComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Beneficiário' } },
				path: 'beneficiarios/cadastro/:bId',
				component: BeneficiaryRegistrationComponent,
			},
			{
				data: {
					pageTitle: { label: 'Editar Beneficiário' },
				},
				path: 'beneficiarios/editar/:bId',
				component: BeneficiaryEditComponent,
			},
			{
				data: { pageTitle: { label: 'Histórico de Beneficiário' } },
				path: `beneficiarios/:beneficiaryId/historico`,
				component: BeneficiaryHistoryComponent,
			},
			{
				data: { pageTitle: { label: 'Beneficiário' } },
				path: 'beneficiarios/:bId',
				component: BeneficiaryViewComponent,
			},
			{
				data: {
					pageTitle: { label: 'Benefícios' },
				},
				path: 'beneficios',
				component: BenefitsComponent,
			},
			{
				data: {
					pageTitle: { label: 'Cadastrar Benefício' },
				},
				path: 'beneficios/cadastro',
				component: BenefitRegistrationComponent,
			},
			{
				data: {
					pageTitle: { label: 'Editar Benefício' },
				},
				path: 'beneficios/:bId',
				component: BenefitRegistrationComponent,
			},
			{
				data: {
					pageTitle: { label: 'Dados Cadastrais' },
				},
				path: 'cadastro',
				component: OrgRegistrationComponent,
			},
			{
				data: {
					pageTitle: { label: 'Cargos' },
				},
				path: 'cargos',
				component: PositionsComponent,
			},
			{
				data: {
					pageTitle: { label: 'Cadastrar Cargo' },
				},
				path: 'cargos/cadastro',
				component: PositionRegistrationComponent,
			},
			{
				data: {
					pageTitle: { label: 'Editar Cargo' },
				},
				path: 'cargos/:cId',
				component: PositionRegistrationComponent,
			},
			{
				canActivate: [permissionGuard],
				path: 'financeiro',
				loadChildren: async (): Promise<Type<FinancialModule>> =>
					import('../../modules/financial/financial.module').then(
						(loadedModule) => loadedModule.FinancialModule,
					),
			},
			{
				data: { pageTitle: { label: 'Painel de Gestão' } },
				path: 'painel',
				component: OrgReportsComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Link' } },
				path: 'painel/cadastro',
				component: OrgReportsLinkComponent,
			},
			{
				data: { pageTitle: { label: 'Editar Link' } },
				path: 'painel/:lId',
				component: OrgReportsLinkComponent,
			},
			{
				data: { pageTitle: { label: 'Parceiros' } },
				path: 'parceiros',
				component: PartnersComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Parceiro' } },
				path: 'parceiros/cadastro',
				component: PartnerRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Editar Parceiro' } },
				path: 'parceiros/:pId',
				component: PartnerRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Corretoras' } },
				path: 'corretoras',
				component: PartnerBrokersComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Corretora' } },
				path: 'corretoras/cadastro',
				component: PartnerBrokerRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Editar Corretora' } },
				path: 'corretoras/:pId',
				component: PartnerBrokerRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Políticas' } },
				path: 'politicas',
				component: PoliciesComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Política' } },
				path: 'politicas/cadastro',
				component: PolicyRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Editar Política' } },
				path: 'politicas/:politicaTipo/:politicaId',
				component: PolicyRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Score Populacional' } },
				path: 'score',
				component: ScoreComponent,
			},
			{
				data: { pageTitle: { label: 'Usuários' } },
				path: 'usuarios',
				component: UsersComponent,
			},
			{
				data: { pageTitle: { label: 'Cadastrar Usuário' } },
				path: 'usuarios/cadastro',
				component: UserRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Editar Usuário' } },
				path: 'usuarios/:ut/:uId',
				component: UserRegistrationComponent,
			},
			{
				data: { pageTitle: { label: 'Segurança' } },
				path: 'seguranca',
				component: SecurityComponent,
			},
			{
				data: { pageTitle: { label: 'Hospital Digital' } },
				path: 'hospital-digital',
				component: DigitalHospitalComponent,
			},
			{
				data: { pageTitle: { label: 'Corretoras Parceiras' } },
				path: 'corretoras-parceiras',
				component: PartnerBrokersComponent,
			},
		],
	},
];

@NgModule({
	exports: [RouterModule],
	imports: [RouterModule.forChild(routes)],
})
export class OrganizationsRoutingModule {}
